import { DateRange, isNullOrUndefined, UTC} from 'rewire-common';
import {
  Modal,
  Form,
  IFormContext,
  validator,
  field,
}                                           from 'rewire-ui';
import {
  IJob,
  IArea,
  FieldFilter,
  isMultiple,
  getMultipleObject,
  getMultiple,
}                                           from '../DataTypes';
import { getNameSearcher }                  from '../utility/Searchers';
import scheduleModel                        from './ScheduleModel';

export class DuplicateJobsDialogModel extends Modal {
  form:  Form;
  jobs?: IJob[];

  constructor() {
    super('Duplicate Jobs');

    const groupFilter        = () => this.form.field.area.value ? {field: 'areaId', eq: this.form.field.area.value?.id} as FieldFilter : undefined;
    const areaSearcher       = getNameSearcher('areas');
    const groupSearcher      = getNameSearcher('groups', groupFilter);
    const occupationSearcher = getNameSearcher('occupations');
    const generatorSearcher  = getNameSearcher('generators');

    this.form = Form.create((form: IFormContext) => ({
      area:                               form.reference(areaSearcher).label('Area').validators('required').onValueChange(this.handleAreaChange).autoFocus(),
      group:                              form.reference(groupSearcher).label('Group').validators('required'),
      occupation:                         form.reference(occupationSearcher).label('Occupation').validators('required'),
      autoCreateVacancyForAbsenceReasons: form.boolean().label('Auto Create Vacancy for Absence Reasons'),
      removeOverlappingAssignments:       form.boolean().label('Remove Overlapping Assignments'),
      effectiveStart:                     form.date().label('Effective Start Date').validators('required').endAdornment(() => null as any),
      effectiveEnd:                       form.date().label('Effective End Date').validators(validator('>=', field('effectiveStart'))).endAdornment(() => null as any),
      generator:                          form.reference(generatorSearcher).label('Shift Pattern'),
      cycleOffset:                        form.number().label('Cycle Offset'),
    }));

    this.action('cancel')
        .action('save', this.submit, { variant: 'contained', type: 'submit' });
  }

  reset() {
    this.form.reset();
    this.jobs = undefined;
  }

  load(jobs: IJob[], startDate: UTC) {
    this.reset();
    if (!jobs?.length) {
      return;
    }
    this.jobs         = jobs;
    const occupation  = this.getOccupationValue(jobs);
    const generator   = this.getGeneratorValue(jobs);
    const cycleOffset = this.getCycleOffsetValue(jobs);

    this.form.value = {
      area:                               jobs[0].group.area,
      group:                              jobs[0].group,
      occupation:                         occupation,
      autoCreateVacancyForAbsenceReasons: true,
      removeOverlappingAssignments:       true,
      effectiveStart:                     startDate,
      effectiveEnd:                       undefined,
      generator:                          generator,
      cycleOffset:                        cycleOffset,
    };
    this.open();
  }

  handleAreaChange = (_form: Form, v?: IArea) => {
    if (!isNullOrUndefined(v)) {
      this.form.field.group.value = undefined;
    }
  };

  private getOccupationValue(jobs: IJob[]) {
    let occupation = undefined;
    for (const job of jobs) {
      if (!(occupation?.id)) {
        occupation = job.occupation;
      } else if (job.occupation?.id && occupation.id !== job.occupation.id) {
        occupation = getMultipleObject();
        break;
      }
    }

    return occupation;
  }

  private getGeneratorValue(jobs: IJob[]) {
    let generator = undefined;
    for (const job of jobs) {
      if (!(generator?.id)) {
        generator = job.generator;
      } else if (job.generator?.id && generator.id !== job.generator.id) {
        generator = getMultipleObject();
        break;
      }
    }

    return generator;
  }

  private getCycleOffsetValue(jobs: IJob[]) {
    let cycleOffset = undefined;
    for (const job of jobs) {
      if (isNullOrUndefined(cycleOffset)) {
        cycleOffset = job.cycleOffset;
      } else if (!isNullOrUndefined(job.cycleOffset) && cycleOffset !== job.cycleOffset) {
        cycleOffset = getMultiple();
        break;
      }
    }

    return cycleOffset;
  }

  private getJobInLastPosition(jobs: IJob[]) {
    return jobs.reduce((prev: IJob, curr: IJob) => {
      if (curr.position > prev.position) {
        return curr;
      }
      return prev;
    }, jobs[0]);
  }

  private save = async (): Promise<boolean> => {
    if (!this.jobs?.length) {
      return false;
    }
    const area                               = this.form.field.area.value;
    const group                              = this.form.field.group.value;
    const occupation                         = this.form.field.occupation.value;
    const generator                          = this.form.field.generator.value;
    const cycleOffset                        = this.form.field.cycleOffset.value;
    const effective                          = new DateRange(this.form.field.effectiveStart.value, this.form.field.effectiveEnd.value);
    const autoCreateVacancyForAbsenceReasons = this.form.field.autoCreateVacancyForAbsenceReasons.value ?? true;
    const removeOverlappingAssignments       = this.form.field.removeOverlappingAssignments.value ?? true;

    const occupationIsMultiple               = isMultiple(occupation);
    const generatorIsMultiple                = isMultiple(generator);
    const cycleOffsetIsMultiple              = isMultiple(cycleOffset);

    const duplicateJobsData: any[] = [];

    this.jobs.forEach((job: IJob) => {
      duplicateJobsData.push({
        area:        area,
        group:       group,
        occupation:  occupationIsMultiple ? job.occupation : occupation,
        generator:   generatorIsMultiple ? job.generator : generator,
        cycleOffset: cycleOffsetIsMultiple ? job.cycleOffset : cycleOffset,
        effective:   effective,
        position:    job.position,
        fields:      {'_autoCreateVacancyForAbsenceReasons:b': autoCreateVacancyForAbsenceReasons},
      });
    });

    // sort by position in desc order, such that when inserting them with the same prev job id, they appear in asc order.
    duplicateJobsData.sort((j1, j2) => j1.position < j2.position ? 1 : (j1.position > j2.position ? -1 : 0));

    if (await scheduleModel.duplicateJobs(duplicateJobsData, removeOverlappingAssignments, this.getJobInLastPosition(this.jobs).id)) {
      return true;
    }
    return false;
  };

  submit = async () => {
    return await this.form.submitAsync(this.save);
  };
}

const duplicateJobsDialogModel = new DuplicateJobsDialogModel();
export default duplicateJobsDialogModel;
